/**
 * GoalScoreSection — PR-7a, Evaluation Results "Goal Score" surface.
 *
 * Renders ONLY when the evaluation carries at least one `goals[]` item or a
 * non-null `total_score` — otherwise this component returns `null` and the
 * page is byte-identical to today (pinned by
 * `GoalScoreSection.goalless.test.tsx`). This mirrors the flag-off/no-eligible-
 * Goal behavior documented in `docs/API_BACKEND.md` § "Goal Score": every
 * evaluation gets `null`/`[]` on these fields until a template configures an
 * eligible Goal and `SPORTFORM_GOAL_SCORE` is armed.
 *
 * Two pieces:
 *   <GoalScoreHero>  — the Goal bonus pill + the Total arithmetic line,
 *                       placed directly under the existing Form hero ring
 *                       (EvalResultsHero, unchanged). Form stays the
 *                       existing 0-100 ring; Goal and Total get their own
 *                       treatment per F §3 / the v5 mockup (post-H2-curve
 *                       ruling): `Total = Goals × Form effect`, NOT the
 *                       superseded v1 "Form + Goal" additive line.
 *   <GoalScoreCards>  — one card per Goal, four states (Exceeded / Met /
 *                       Missed / Could not measure) — a Goal card is
 *                       visually distinct from a Check card (GoalResultsSection,
 *                       unchanged) and sits in ITS OWN section, never merged
 *                       with the six existing Check categories.
 *
 * Vocabulary: "Goal" here means ONLY the new competitive target (Distance /
 * Speed / Angle). The existing six checkpoint types stay "Checks" — see
 * CLAUDE.md § Goal Score / `docs/goal_score/F_ui_ux_designs.md` §1. Never
 * mix the two words in copy added to this file.
 */
import { TrophyIcon, CheckCircleIcon, MinusCircleIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import type { GoalScoreItem } from '@/types/goalScore';
import {
  GOAL_CARD_STATE_LABEL,
  exactZeroBonusPointsLabel,
  exactZeroGoalBonusCopy,
  formatGoalMeasuredVsTarget,
  goalBreakdownSubline,
  goalCardAbstentionCopy,
  goalCardBonusLine,
  goalCardState,
  goalsUnmeasuredCopy,
  noGoalConfiguredCopy,
  totalArithmeticLine,
  type GoalCardState,
} from '@/utils/goalScoreCopy';

// ─── Shared gate ─────────────────────────────────────────────────────────────

export interface GoalScoreSectionProps {
  /** Sum of per-Goal bonuses. `null` when nothing eligible measured. */
  goalBonusScore: number | null | undefined;
  /** The Form-effect curve's multiplier at this evaluation's Form score. `null` together with `totalScore`. */
  formEffect: number | null | undefined;
  /** `goalBonusScore * formEffect`. `null` iff `goalBonusScore` is `null`. */
  totalScore: number | null | undefined;
  /** The existing Form score (0-100), for the arithmetic line only — never re-rendered as a second ring here. */
  formScore: number;
  goals: GoalScoreItem[] | undefined;
  className?: string;
}

/** True when there is anything Goal-Score-related to show at all. */
function hasGoalScoreContent(goals: GoalScoreItem[] | undefined, totalScore: number | null | undefined): boolean {
  return (goals != null && goals.length > 0) || totalScore != null;
}

export default function GoalScoreSection({
  goalBonusScore,
  formEffect,
  totalScore,
  formScore,
  goals,
  className,
}: GoalScoreSectionProps) {
  if (!hasGoalScoreContent(goals, totalScore)) return null;

  const goalList = goals ?? [];

  return (
    <div data-testid="goal-score-section" className={cn('space-y-4', className)}>
      <GoalScoreHero
        goalBonusScore={goalBonusScore ?? null}
        formEffect={formEffect ?? null}
        totalScore={totalScore ?? null}
        formScore={formScore}
        goals={goalList}
      />
      {goalList.length > 0 && <GoalScoreCards goals={goalList} />}
    </div>
  );
}

// ─── Hero: Goal pill + Total arithmetic ───────────────────────────────────────

interface GoalScoreHeroProps {
  goalBonusScore: number | null;
  formEffect: number | null;
  totalScore: number | null;
  formScore: number;
  goals: GoalScoreItem[];
}

export function GoalScoreHero({ goalBonusScore, formEffect, totalScore, formScore, goals }: GoalScoreHeroProps) {
  const noGoalAtAll = goals.length === 0 && totalScore == null;
  const unmeasuredThisEval = goals.length > 0 && totalScore == null;
  // Goal Score go-live B4: every configured Goal was measured (no
  // abstentions) AND the summed bonus is EXACTLY 0 (not null/undefined —
  // that's `unmeasuredThisEval` above). `=== 0` deliberately excludes a
  // near-zero float; the backend sums per-goal bonuses via log1p, which is
  // exactly 0.0 only when every goal's excess_z floors at 0 (met or missed
  // the bar, never exceeded it) — see goal_scoring.compute_goal_bonus.
  const allMeasured = goals.length > 0 && goals.every((g) => g.score !== null);
  const exactZeroBonus = allMeasured && goalBonusScore === 0;

  return (
    <div
      data-testid="goal-score-hero"
      className="rounded-lg border border-border-subtle bg-dark-elevated-1 p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <TrophyIcon className="h-4 w-4 text-[#9D6FFF]" aria-hidden="true" />
        <h3 className="font-heading text-xs font-semibold uppercase tracking-widest text-text-muted">
          Goal score
        </h3>
      </div>

      {noGoalAtAll && (
        <p data-testid="goal-score-no-goal" className="text-sm text-text-secondary">
          {noGoalConfiguredCopy()}
        </p>
      )}

      {unmeasuredThisEval && (
        <p data-testid="goal-score-unmeasured" className="text-sm text-text-secondary">
          {goalsUnmeasuredCopy()}
        </p>
      )}

      {/* Goal Score go-live B4, corrected by owner ruling G-2 (2026-10-29,
          red-team finding F3, option A): the exact-zero case shows the
          plain-language sentence ALONGSIDE the honest numbers — bonus 0,
          Total 0 — never in place of them. Substituting formScore for the
          real (zero) Total, as the original B4 did, let the stored/ranked
          Total look like it had silently become the Form score; that is
          exactly what this correction removes. The pill and Total block
          below are UNCONDITIONAL (same markup for every bonus value,
          including exactly 0) so there is only one honest rendering path. */}
      {exactZeroBonus && (
        <p data-testid="goal-score-exact-zero" className="text-sm font-semibold text-text-primary">
          {exactZeroGoalBonusCopy()}
        </p>
      )}

      {goalBonusScore != null && (
        <div className="flex flex-wrap items-baseline gap-3">
          <span
            data-testid="goal-score-bonus-pill"
            className="inline-flex items-center rounded-full px-3 py-1 text-base font-semibold"
            style={{ backgroundColor: 'rgba(157,111,255,0.14)', color: '#9D6FFF' }}
          >
            +{Math.round(goalBonusScore * 10) / 10}
          </span>
          {exactZeroBonus ? (
            // G-2: explicitly labelled "bonus points" so this 0 can never
            // be misread as the Form score sitting under the Total label
            // below.
            <span data-testid="goal-score-exact-zero-bonus-caption" className="text-xs text-text-muted">
              {exactZeroBonusPointsLabel()}
            </span>
          ) : (
            goals.length > 0 && (
              <span className="text-xs text-text-muted">{goalBreakdownSubline(goals)}</span>
            )
          )}
        </div>
      )}

      {totalScore != null && goalBonusScore != null && formEffect != null && (
        <div data-testid="goal-score-total" className="pt-1">
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-3xl font-bold text-text-primary tabular-nums">
              {Math.round(totalScore * 10) / 10}
            </span>
            <span className="text-xs uppercase tracking-wide text-text-muted">Total score</span>
          </div>
          <p data-testid="goal-score-arithmetic" className="mt-1 text-xs text-text-secondary">
            {totalArithmeticLine(goalBonusScore, formEffect, formScore, totalScore)}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Per-Goal cards ────────────────────────────────────────────────────────────

const STATE_ICON: Record<GoalCardState, typeof TrophyIcon> = {
  exceeded: TrophyIcon,
  met: CheckCircleIcon,
  missed: MinusCircleIcon,
  cannot_measure: EyeSlashIcon,
};

const STATE_BADGE_CLASS: Record<GoalCardState, string> = {
  exceeded: 'bg-success/15 text-success',
  met: 'bg-accent/15 text-accent',
  missed: 'bg-warning/15 text-warning',
  cannot_measure: 'bg-dark-elevated-2 text-text-muted',
};

export interface GoalScoreCardsProps {
  goals: GoalScoreItem[];
  className?: string;
}

export function GoalScoreCards({ goals, className }: GoalScoreCardsProps) {
  return (
    <div data-testid="goal-score-cards" className={cn('space-y-2', className)}>
      {goals.map((item) => (
        <GoalScoreCard key={item.goal_key} item={item} />
      ))}
    </div>
  );
}

function GoalScoreCard({ item }: { item: GoalScoreItem }) {
  const state = goalCardState(item);
  const Icon = STATE_ICON[state];
  const measuredLine = formatGoalMeasuredVsTarget(item);
  const bonusLine = goalCardBonusLine(item, state);
  const label = item.label ?? 'Goal';

  return (
    <div
      data-testid="goal-score-card"
      data-goal-state={state}
      className="relative rounded-lg border border-border-subtle bg-dark-elevated-1/80 overflow-hidden"
    >
      {/* Left accent strip — indigo (reuses --results-dot-mirror's hue, the
          existing "Goal"/competitive-metric color already used elsewhere
          in the results theme) so a Goal card reads visually distinct from
          a Check card (GoalResultsSection's own category colors). */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ backgroundColor: '#9D6FFF' }}
        aria-hidden="true"
      />
      <div className="flex items-start gap-3 pl-4 pr-3 py-3">
        <Icon className="h-5 w-5 flex-shrink-0 text-text-muted mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-sm text-text-primary truncate">{label}</span>
            <span
              className={cn(
                'text-[10px] font-medium px-1.5 py-px rounded-full flex-shrink-0 uppercase tracking-wide',
                STATE_BADGE_CLASS[state],
              )}
            >
              {GOAL_CARD_STATE_LABEL[state]}
            </span>
          </div>
          {state === 'cannot_measure' ? (
            <p data-testid="goal-score-card-abstention" className="text-xs leading-relaxed text-text-secondary">
              {goalCardAbstentionCopy(item)}
            </p>
          ) : (
            <p className="text-xs leading-relaxed text-text-secondary">
              {measuredLine}
              {bonusLine ? <> <strong className="text-text-primary">{bonusLine}</strong></> : null}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
